export class StatsTracker {
	mobsKilledByType: Map<string, number> = new Map();
	damageReceivedBySource: Map<string, number> = new Map();
	shotsFiredByWeapon: Map<string, number> = new Map();
	ammoCollectedByWeapon: Map<string, number> = new Map();
	itemsCollectedByType: Map<string, number> = new Map();
	killsByWeaponKey: Map<string, number> = new Map();
	tilesWalked: number = 0;
	containersOpened: number = 0;
	obstaclesDestroyed: number = 0;
	spawnersDestroyed: number = 0;
	healingReceived: number = 0;
	goldCollected: number = 0;
	lowestHpReached: number = -1;
	private parent: StatsTracker | null = null;

	setParent(parent: StatsTracker): void {
		this.parent = parent;
	}

	private incrementMap(map: Map<string, number>, key: string, amount: number = 1): void {
		map.set(key, (map.get(key) ?? 0) + amount);
	}

	recordMobKill(mobKey: string): void {
		this.incrementMap(this.mobsKilledByType, mobKey);
		this.parent?.recordMobKill(mobKey);
	}

	recordDamageReceived(sourceKey: string, amount: number): void {
		this.incrementMap(this.damageReceivedBySource, sourceKey, amount);
		this.parent?.recordDamageReceived(sourceKey, amount);
	}

	recordShotFired(weaponKey: string): void {
		this.incrementMap(this.shotsFiredByWeapon, weaponKey);
		this.parent?.recordShotFired(weaponKey);
	}

	recordAmmoCollected(weaponKey: string, amount: number): void {
		this.incrementMap(this.ammoCollectedByWeapon, weaponKey, amount);
		this.parent?.recordAmmoCollected(weaponKey, amount);
	}

	recordItemCollected(itemKey: string, amount: number): void {
		this.incrementMap(this.itemsCollectedByType, itemKey, amount);
		this.parent?.recordItemCollected(itemKey, amount);
	}

	recordKillByWeapon(weaponKey: string): void {
		this.incrementMap(this.killsByWeaponKey, weaponKey);
		this.parent?.recordKillByWeapon(weaponKey);
	}

	recordTileWalked(): void {
		this.tilesWalked++;
		this.parent?.recordTileWalked();
	}

	recordContainerOpened(): void {
		this.containersOpened++;
		this.parent?.recordContainerOpened();
	}

	recordObstacleDestroyed(): void {
		this.obstaclesDestroyed++;
		this.parent?.recordObstacleDestroyed();
	}

	recordSpawnerDestroyed(): void {
		this.spawnersDestroyed++;
		this.parent?.recordSpawnerDestroyed();
	}

	recordHealing(amount: number): void {
		this.healingReceived += amount;
		this.parent?.recordHealing(amount);
	}

	recordGoldCollected(amount: number): void {
		this.goldCollected += amount;
		this.parent?.recordGoldCollected(amount);
	}

	recordHpSnapshot(currentHp: number): void {
		if (this.lowestHpReached === -1 || currentHp < this.lowestHpReached) {
			this.lowestHpReached = currentHp;
		}
		this.parent?.recordHpSnapshot(currentHp);
	}

	serialize(): Record<string, unknown> {
		return {
			mobsKilledByType: Object.fromEntries(this.mobsKilledByType),
			damageReceivedBySource: Object.fromEntries(this.damageReceivedBySource),
			shotsFiredByWeapon: Object.fromEntries(this.shotsFiredByWeapon),
			ammoCollectedByWeapon: Object.fromEntries(this.ammoCollectedByWeapon),
			itemsCollectedByType: Object.fromEntries(this.itemsCollectedByType),
			killsByWeaponKey: Object.fromEntries(this.killsByWeaponKey),
			tilesWalked: this.tilesWalked,
			containersOpened: this.containersOpened,
			obstaclesDestroyed: this.obstaclesDestroyed,
			spawnersDestroyed: this.spawnersDestroyed,
			healingReceived: this.healingReceived,
			goldCollected: this.goldCollected,
			lowestHpReached: this.lowestHpReached,
		};
	}

	static deserialize(data: any): StatsTracker {
		const tracker = new StatsTracker();
		tracker.mobsKilledByType = new Map(Object.entries(data.mobsKilledByType ?? {}));
		tracker.damageReceivedBySource = new Map(Object.entries(data.damageReceivedBySource ?? {}));
		tracker.shotsFiredByWeapon = new Map(Object.entries(data.shotsFiredByWeapon ?? {}));
		tracker.ammoCollectedByWeapon = new Map(Object.entries(data.ammoCollectedByWeapon ?? {}));
		tracker.itemsCollectedByType = new Map(Object.entries(data.itemsCollectedByType ?? {}));
		tracker.killsByWeaponKey = new Map(Object.entries(data.killsByWeaponKey ?? {}));
		tracker.tilesWalked = data.tilesWalked ?? 0;
		tracker.containersOpened = data.containersOpened ?? 0;
		tracker.obstaclesDestroyed = data.obstaclesDestroyed ?? 0;
		tracker.spawnersDestroyed = data.spawnersDestroyed ?? 0;
		tracker.healingReceived = data.healingReceived ?? 0;
		tracker.goldCollected = data.goldCollected ?? 0;
		tracker.lowestHpReached = data.lowestHpReached ?? -1;
		return tracker;
	}
}
